/* openings.js — door/window opening geometry + same-wall obstruction + editor ops.
 * Ported from lhc-app.html: _openingAABB (~31822), _doorSwingAABB (~31848),
 * _cabOpeningBlock (~31943), addWallOpening (~50394), clampOpeningToWall (~50430),
 * opening field commit / open / hinge toggles (renderWallOpeningsList ~50449 +
 * renderSelectedOpeningInto wireNum ~50188), refreshOpenings3D (~50982, 100ms debounce —
 * LOCK-EPE-13). Opening model: { id, wallId, u(m), width(m), height(m), sill(m), type,
 * open?, hinge? }. Editor ops mutate the passed state in place (engine semantics).
 */
import { WALL_EPS, DEFAULT_WALL_HEIGHT } from "./constants";
import { getWallPlacementInfo } from "./placement";
import { effectiveThickness } from "./wallJoins";

/** _openingAABB(op, walls): world box of a window/door in the wall band. (~31822) */
/* OPENING DEFAULTS (2026-08-18 hardcode purge): the door/window boot sizes
 * used to be re-typed in four places (create, two clamp paths, hit-test) plus
 * a fourth sill copy in outputs.js — one constant each now. */
export const OPENING_DEFAULTS = {
  doorWidthM: 0.90, doorHeightM: 2.10,
  windowWidthM: 1.20, windowHeightM: 1.50, windowSillM: 0.90,
};

export function openingAABB(op, walls) {
  const wall = walls.find((w) => w.id === op.wallId);
  if (!wall) return null;
  const info = getWallPlacementInfo(wall, walls);
  if (!info) return null;
  const along = op.u + op.width / 2;
  const cx = wall.start[0] + info.dirX * along;
  const cz = wall.start[1] + info.dirZ * along;
  const t = effectiveThickness(wall) / 2;
  const halfAlong = op.width / 2;
  let minX, maxX, minZ, maxZ;
  if (Math.abs(info.dirX) > Math.abs(info.dirZ)) {
    minX = cx - halfAlong; maxX = cx + halfAlong; minZ = cz - t; maxZ = cz + t;
  } else {
    minZ = cz - halfAlong; maxZ = cz + halfAlong; minX = cx - t; maxX = cx + t;
  }
  const minY = op.type === "door" ? 0 : (op.sill != null ? op.sill : OPENING_DEFAULTS.windowSillM);
  const maxY = minY + op.height;
  return { minX, maxX, minZ, maxZ, minY, maxY, wall, op };
}

/** _doorSwingAABB(op, walls): conservative width×width keep-clear band into the room. (~31848) */
export function doorSwingAABB(op, walls) {
  if (op.type !== "door") return null;
  const wall = walls.find((w) => w.id === op.wallId);
  if (!wall) return null;
  const info = getWallPlacementInfo(wall, walls);
  if (!info) return null;
  const along = op.u + op.width / 2;
  const cx = wall.start[0] + info.dirX * along;
  const cz = wall.start[1] + info.dirZ * along;
  const t = effectiveThickness(wall) / 2;
  const halfAlong = op.width / 2;
  const innerX = cx + info.nx * t, innerZ = cz + info.nz * t;
  const farX = innerX + info.nx * op.width, farZ = innerZ + info.nz * op.width;
  let minX, maxX, minZ, maxZ;
  if (Math.abs(info.dirX) > Math.abs(info.dirZ)) {
    minX = cx - halfAlong; maxX = cx + halfAlong; minZ = Math.min(innerZ, farZ); maxZ = Math.max(innerZ, farZ);
  } else {
    minZ = cz - halfAlong; maxZ = cz + halfAlong; minX = Math.min(innerX, farX); maxX = Math.max(innerX, farX);
  }
  return { minX, maxX, minZ, maxZ, minY: 0, maxY: op.height, op, wall };
}

// ── Editor operations (WI-5) ────────────────────────────────────────────────
// The prototype's editors (wall-props openings list + elevation right panel) share these.
// Callers own history snapshots: the prototype pushes wallHistory ONLY for the elevation
// panel's delete (LOCK-EPE-12) — never for add / field edits / list deletes.

/** clampOpeningToWall(op, walls): keep an opening within its wall — width no wider than
 *  the wall, offset so the whole opening stays on it, top edge clamped to wall height.
 *  Used by both the plan (wall-props) and elevation editors so they behave identically.
 *  (~50430, verbatim; `walls` passed explicitly instead of the global) */
export function clampOpeningToWall(op, walls) {
  const wall = walls.find((w) => w.id === op.wallId);
  if (!wall) return;
  const wallLen = Math.hypot(wall.end[0] - wall.start[0], wall.end[1] - wall.start[1]);
  const wallH = wall.height || DEFAULT_WALL_HEIGHT;
  op.width = Math.max(0.05, Math.min(op.width || 0, wallLen));
  op.u     = Math.max(0, Math.min(op.u || 0, wallLen - op.width));
  if (op.height != null) op.height = Math.max(0.05, op.height);
  if (op.sill   != null) op.sill   = Math.max(0, op.sill);
  // Clamp opening top edge to wall height
  if (op.type === "door") {
    op.height = Math.min(op.height || OPENING_DEFAULTS.doorHeightM, wallH);
  } else {
    const sill = op.sill != null ? op.sill : 0;
    op.height  = Math.min(op.height || OPENING_DEFAULTS.windowHeightM, Math.max(0.05, wallH - sill));
    op.sill    = Math.min(sill, Math.max(0, wallH - (op.height || 0.05)));
  }
}

/** addWallOpening(state, type, wallId): create a door/window on a wall with the engine's
 *  defaults and push it onto state.wallOpenings. Returns { ok, opening?, reason? }.
 *  v0.33 (LH C4): a peninsula is an imaginary guide line — no doors/windows. (~50394) */
export function addWallOpening(state, type, wallId) {
  if (!wallId) return { ok: false, reason: "No wall selected" };
  const wall = state.walls.find((w) => w.id === wallId);
  if (!wall) return { ok: false, reason: "Unknown wall " + wallId };
  if ((wall.style || "standard") === "peninsula") {
    return { ok: false, reason: "A peninsula guide cannot hold doors or windows" };
  }
  const wallLen = Math.hypot(wall.end[0] - wall.start[0], wall.end[1] - wall.start[1]);
  const id = "op_" + Date.now() + "_" + Math.random().toString(36).slice(2, 6);
  const w = (type === "door") ? OPENING_DEFAULTS.doorWidthM : OPENING_DEFAULTS.windowWidthM;
  // Place ~30% along the wall, but keep the whole opening on the wall even on
  // short walls (the old formula could go negative and start off the wall).
  const baseU = Math.max(0, Math.min(wallLen * 0.3, wallLen - w));
  const op = type === "door"
    ? { id, wallId, type, u: baseU, width: w, height: OPENING_DEFAULTS.doorHeightM, open: true, hinge: "left" }
    : { id, wallId, type, u: baseU, width: w, height: OPENING_DEFAULTS.windowHeightM, sill: OPENING_DEFAULTS.windowSillM };
  clampOpeningToWall(op, state.walls);
  state.wallOpenings.push(op);
  return { ok: true, opening: op };
}

/** setOpeningField(state, id, field, rawMm): the shared numeric commit — mm → metres,
 *  clamp to the wall, and report the (possibly clamped) mm value to reflect back into the
 *  input. Non-finite input is rejected with the current value. (wireNum commit ~50188 /
 *  list commit ~50510; LOCK-EPE-10: clampOpeningToWall after EVERY field change) */
export function setOpeningField(state, id, field, rawMm) {
  const op = state.wallOpenings.find((o) => o.id === id);
  if (!op) return { ok: false, valueMm: 0 };
  if (!Number.isFinite(rawMm)) return { ok: false, valueMm: Math.round((op[field] ?? 0) * 1000) };
  op[field] = rawMm / 1000;
  clampOpeningToWall(op, state.walls);
  return { ok: true, valueMm: Math.round((op[field] ?? 0) * 1000), opening: op };
}

/** Toggle a door open/closed: op.open = (op.open === false). (~50550) */
export function toggleOpeningOpen(state, id) {
  const op = state.wallOpenings.find((o) => o.id === id);
  if (!op) return { ok: false };
  op.open = (op.open === false);
  return { ok: true, opening: op };
}

/** Toggle a door's hinge side left/right. (~50569) */
export function toggleOpeningHinge(state, id) {
  const op = state.wallOpenings.find((o) => o.id === id);
  if (!op) return { ok: false };
  op.hinge = (op.hinge === "right") ? "left" : "right";
  return { ok: true, opening: op };
}

/** Door OPENING DIRECTION (user spec): "in" (default — swings into the room) |
 *  "out" (swings outward). Persisted on the opening; the plan symbol and the
 *  3D door leaf both honour it. */
export function toggleOpeningDir(state, id) {
  const op = state.wallOpenings.find((o) => o.id === id);
  if (!op) return { ok: false };
  op.dir = (op.dir === "out") ? "in" : "out";
  return { ok: true, opening: op };
}

/** Remove an opening by id (splice — callers push history BEFORE when required,
 *  LOCK-EPE-12). Returns { ok, opening? }. */
export function removeOpening(state, id) {
  const idx = state.wallOpenings.findIndex((o) => o.id === id);
  if (idx < 0) return { ok: false };
  const [op] = state.wallOpenings.splice(idx, 1);
  return { ok: true, opening: op };
}

/** Openings on one wall (render order preserved). */
export function openingsForWall(openings, wallId) {
  return (openings || []).filter((o) => o.wallId === wallId);
}

/** createOpenings3DRefresher(rebuild): refreshOpenings3D port — returns a debounced (100ms)
 *  trigger for the 3D wall rebuild. Rapid opening edits collapse into one rebuild.
 *  (~50982; LOCK-EPE-13: never bypass the debounce for live edits) */
export function createOpenings3DRefresher(rebuild) {
  let timer = null;
  return function refreshOpenings3D() {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => { if (typeof rebuild === "function") rebuild(); }, 100);
  };
}

/** _cabOpeningBlock(candidate, cBox, openings): same-wall door/window the cabinet would
 *  block. Returns 'the door' | 'the window' | null. (~31943) */
export function cabOpeningBlock(candidate, cBox, openings) {
  if (!Array.isArray(openings) || !candidate.wallId) return null;
  const def = cBox && cBox.def;
  if (!def) return null;
  const EPS = WALL_EPS;
  const aL = candidate.absLeft, aR = aL + def.width;
  for (const op of openings) {
    if (op.wallId !== candidate.wallId) continue;
    const oL = op.u, oR = op.u + op.width;
    const hOv = Math.min(aR, oR) - Math.max(aL, oL);
    if (hOv <= EPS) continue;
    const oBot = op.type === "window" ? (op.sill || 0) : 0;
    const oTop = oBot + (op.height || OPENING_DEFAULTS.doorHeightM);
    const vOv = Math.min(cBox.maxY, oTop) - Math.max(cBox.minY, oBot);
    if (vOv <= EPS) continue;
    return op.type === "door" ? "the door" : "the window";
  }
  return null;
}
